<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import { useAccountAccessRestriction, useReInitiateKycMutation } from '@modules/account'
import { useDashboardState, type DashboardState } from '@modules/dashboard'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Popover from '@uiKit/elements/Popover.vue'
import Tooltip from '@uiKit/elements/Tooltip.vue'
import NavbarProfileMenu from './NavbarProfileMenu.vue'
import logo from '@assets/images/logo.png'
import slicealText from '@assets/images/sliceal-text.png'

interface Props {
  unreadCount?: number
}

withDefaults(defineProps<Props>(), {
  unreadCount: 0,
})

const emit = defineEmits<{
  (e: 'toggle-mobile-nav'): void
  (e: 'open-notifications'): void
  (e: 'logout'): void
}>()

const router = useRouter()
const profilePopover = ref<InstanceType<typeof Popover> | null>(null)
const dashboardState = useDashboardState()
const { isRestricted } = useAccountAccessRestriction()
const reInitiateMutation = useReInitiateKycMutation()

interface SectionLink {
  label: string
  to: string
  icon: PiIconName
}

// Primary destinations in the top nav — Dashboard leads the set as the
// "home" surface; Properties is the marketplace (was Opportunities); Wallet
// and Market round out the four. Saved properties is now an inline filter
// on the Properties page, not a top-level destination. Help / Settings live
// in utility surfaces, not here.
const allSections: SectionLink[] = [
  { label: 'Dashboard', to: routes.DASHBOARD, icon: 'home' },
  { label: 'Portfolio', to: routes.PORTFOLIO, icon: 'briefcase' },
  { label: 'Properties', to: routes.PROPERTIES, icon: 'building-columns' },
  { label: 'Wallet', to: routes.WALLET, icon: 'wallet' },
  { label: 'Marketplace', to: routes.MARKETPLACE, icon: 'chart-bar' },
]

const isGuest = computed(() => dashboardState.value.kind === 'guest')

// Guest users have no wallet — hide it from the top nav.
const sections = computed(() =>
  isGuest.value
    ? allSections.filter((s) => s.to !== routes.WALLET)
    : allSections,
)

interface KycChip {
  label: string
  tone: 'verified' | 'pending' | 'review' | 'rejected' | 'idle'
  // Where clicking the chip takes the user. Tracked here so the chip stays
  // the canonical "where does this account need to go next?" surface.
  to: string | null
}

// Chip copy + tone derived from the shared DashboardState so the top-nav
// always speaks the same status the dashboard does — single source of truth.
// `invested` and `loading` deliberately map to null — the top-nav reads
// cleaner when investing is the default user state.
const CHIP_FOR_STATE: Record<DashboardState['kind'], KycChip | null> = {
  'onboarding-pending': { label: 'Verify identity', tone: 'pending', to: routes.ONBOARDING_WIZARD },
  'in-review': { label: 'Under review', tone: 'review', to: routes.PROFILE },
  'rekyc-required': { label: 'Renew identity', tone: 'pending', to: routes.ONBOARDING_WIZARD },
  paused: { label: 'Account paused', tone: 'rejected', to: routes.HELP },
  'regional-explore': { label: 'Explore mode', tone: 'review', to: routes.HELP },
  'approved-ready': { label: 'KYC verified', tone: 'verified', to: null },
  guest: null,
  invested: null,
  loading: null,
}

function chipForState(kind: DashboardState['kind']): KycChip | null {
  return CHIP_FOR_STATE[kind]
}

// A blocked-but-verified account renders the normal dashboard (so its state is
// approved-ready / invested), which would otherwise show a green "KYC verified"
// chip. Override it with a red "Account paused" chip — the top-nav status must
// reflect the restriction, not just KYC.
const RESTRICTED_CHIP: KycChip = {
  label: 'Account paused',
  tone: 'rejected',
  to: routes.HELP,
}

const kycChip = computed<KycChip | null>(() =>
  isRestricted.value ? RESTRICTED_CHIP : chipForState(dashboardState.value.kind),
)

const isRekycChip = computed(
  () => !isRestricted.value && dashboardState.value.kind === 'rekyc-required',
)

// Re-KYC fires a network call before navigating, so the chip reflects that
// in-flight state — otherwise a tap reads as "nothing happened".
const isRenewing = computed(() => reInitiateMutation.isPending.value)

const chipTooltip = computed(() => {
  if (kycChip.value?.tone === 'verified') return 'Identity verified'
  if (isRekycChip.value) return 'Renew your identity verification'
  return 'Tap to view details'
})

const handleKycChipClick = async () => {
  // Re-KYC ("Renew identity") needs a primed session before the wizard opens:
  // re-initiate first, then route into the resubmit wizard. Pushing straight to
  // /onboarding mounts the wizard in normal mode, fires POST /onboarding/start,
  // and the backend rejects it ("Onboarding has already been completed") →
  // blank page. Mirrors StateReKycRequiredContainer / useInvestEligibility.
  if (isRekycChip.value) {
    if (reInitiateMutation.isPending.value) return
    try {
      await reInitiateMutation.mutateAsync()
    } catch {
      return
    }
    router.push({ path: routes.ONBOARDING_WIZARD, query: { mode: 'resubmit' } })
    return
  }

  const to = kycChip.value?.to
  if (to) router.push(to)
}
</script>

<template>
  <Box is="header" class="topnav" align="center" justify="between">
    <Box align="center" gap="2" class="topnav__left">
      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__icon-btn topnav__mobile-toggle"
        aria-label="Open menu"
        @click="emit('toggle-mobile-nav')"
      >
        <PiIcon name="bars" />
      </Box>

      <RouterLink :to="routes.DASHBOARD" class="topnav__brand" aria-label="Sliceal home">
        <img :src="logo" width="28" height="28" alt="" class="topnav__brand-icon" />
        <img :src="slicealText" alt="Sliceal" class="topnav__brand-wordmark" />
      </RouterLink>
    </Box>

    <Box is="nav" align="center" gap="1" class="topnav__sections" aria-label="Primary">
      <RouterLink
        v-for="item in sections"
        :key="item.to"
        :to="item.to"
        class="topnav__section-link"
        active-class="topnav__section-link--active"
      >
        <PiIcon :name="item.icon" class="topnav__section-icon" />
        <Text type="body" weight="medium" is="span">{{ item.label }}</Text>
      </RouterLink>
    </Box>

    <Box align="center" gap="2" class="topnav__right">
      <Tooltip v-if="kycChip" :content="chipTooltip">
        <Box
          is="button"
          align="center"
          gap="1"
          class="kyc-chip"
          :class="[`kyc-chip--${kycChip.tone}`, { 'kyc-chip--busy': isRenewing }]"
          :aria-label="kycChip.label"
          :aria-busy="isRenewing"
          @click="handleKycChipClick"
        >
          <Box class="kyc-chip__dot" :class="`kyc-chip__dot--${kycChip.tone}`" />
          <Text type="caption" weight="medium" is="span" class="kyc-chip__label">
            {{ kycChip.label }}
          </Text>
        </Box>
      </Tooltip>

      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__icon-btn topnav__bell"
        aria-label="Notifications"
        @click="emit('open-notifications')"
      >
        <PiIcon name="bell" />
        <Text
          v-if="unreadCount > 0"
          is="span"
          type="caption-sm"
          weight="bold"
          class="topnav__bell-badge"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </Text>
      </Box>

      <Box
        is="button"
        align="center"
        justify="center"
        class="topnav__avatar"
        aria-label="Account menu"
        @click="profilePopover?.toggle($event)"
      >
        <PiIcon name="user" />
      </Box>
    </Box>

    <Popover ref="profilePopover">
      <NavbarProfileMenu
        :is-guest="isGuest"
        @navigate="
          (to) => {
            profilePopover?.hide()
            router.push(to)
          }
        "
        @logout="
          () => {
            profilePopover?.hide()
            emit('logout')
          }
        "
      />
    </Popover>
  </Box>
</template>

<style scoped>
.topnav {
  height: 64px;
  padding: 0 1.5rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
  flex-shrink: 0;
}

.topnav__left,
.topnav__right {
  flex-shrink: 0;
}

.topnav__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  padding: 0.25rem 0.25rem;
  border-radius: var(--p-border-radius-sm);
}

.topnav__brand:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.topnav__brand-wordmark {
  height: 18px;
  width: auto;
  display: block;
}

/* ── Section links (center) ───────────────────── */
.topnav__sections {
  flex: 1;
  justify-content: center;
}

.topnav__section-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  text-decoration: none;
  color: var(--p-text-muted-color);
  transition:
    background 0.15s ease,
    color 0.15s ease;
  min-height: 40px;
}

.topnav__section-link:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.topnav__section-link--active {
  background: var(--p-primary-50);
  color: var(--p-primary-700);
}

.topnav__section-link--active :deep(.topnav__section-icon) {
  color: var(--p-primary-700);
}

.topnav__section-icon {
  font-size: 1rem;
  color: var(--p-text-muted-color);
}

/* ── Utility icon buttons ─────────────────────── */
.topnav__icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  padding: 0;
  position: relative;
}

.topnav__icon-btn:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.topnav__icon-btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.topnav__bell-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background: var(--p-danger-500);
  color: var(--p-surface-0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
  border: 1.5px solid var(--p-surface-0);
}

.topnav__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-50);
  color: var(--p-text-color);
  cursor: pointer;
  padding: 0;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.topnav__avatar:hover {
  background: var(--p-surface-100);
  border-color: var(--p-primary-300);
}

.topnav__avatar:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

/* ── KYC chip ─────────────────────────────────── */
.kyc-chip {
  background: var(--p-surface-50);
  border: 1px solid var(--p-content-border-color);
  border-radius: 999px;
  padding: 0.3rem 0.625rem;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.kyc-chip:hover {
  background: var(--p-surface-100);
}

.kyc-chip--busy {
  opacity: 0.65;
  cursor: progress;
  pointer-events: none;
}

.kyc-chip:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.kyc-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.kyc-chip__dot--verified {
  background: var(--p-success-500);
}

.kyc-chip__dot--pending {
  background: var(--p-warning-500);
  box-shadow: 0 0 0 3px var(--p-warning-100);
}

.kyc-chip__dot--review {
  background: var(--p-info-500);
}

.kyc-chip__dot--rejected {
  background: var(--p-danger-500);
}

.kyc-chip__dot--idle {
  background: var(--p-surface-400);
}

.kyc-chip--verified {
  background: var(--p-success-50);
  border-color: var(--p-success-200);
}

.kyc-chip--verified .kyc-chip__label {
  color: var(--p-success-700);
}

.kyc-chip--pending {
  background: var(--p-warning-50);
  border-color: var(--p-warning-200);
}

.kyc-chip--pending .kyc-chip__label {
  color: var(--p-warning-700);
}

.kyc-chip--review .kyc-chip__label,
.kyc-chip--idle .kyc-chip__label {
  color: var(--p-text-muted-color);
}

.kyc-chip--rejected {
  background: var(--p-danger-50);
  border-color: var(--p-danger-200);
}

.kyc-chip--rejected .kyc-chip__label {
  color: var(--p-danger-700);
}

/* ── Mobile toggle (hidden on desktop) ────────── */
.topnav__mobile-toggle {
  display: none;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
}

@media (max-width: 991px) {
  .topnav__mobile-toggle {
    display: inline-flex;
  }

  .topnav__sections {
    display: none;
  }

  .topnav__brand-wordmark {
    display: none;
  }

  .kyc-chip {
    padding: 0.3rem 0.5rem;
  }
}

@media (max-width: 480px) {
  .kyc-chip__label {
    display: none;
  }

  .kyc-chip {
    padding: 0.4rem;
  }
}

@media (max-width: 640px) {
  .topnav {
    padding: 0 1rem;
  }
}
</style>
